/**
 * The sourcing quality check as files (Sofia, Oct 18 2026: "is what the agent sourced right, and why
 * these candidates and not others?"). One row per person a run looked at, in the order it met them:
 * what happened and why, the verdict and quote per must-have from the LIST card, the same from the
 * FULL profile check where there is one, the recruiter's decision, and the card text itself — so a
 * reviewer can judge every call against what the tool actually saw.
 */
import type { QualityExport } from '../../services/taOutreachApi';

const OUTCOME: Record<string, string> = {
  shortlist: 'Shortlist',
  extra: 'Fits, not added (target reached)',
  not_fit: 'Not a fit',
  contacted: 'Skipped: LinkedIn shows FINN contacted them',
  blocked: 'Skipped: contact register / colleague',
  known: 'Skipped: already on your list',
};

const cell = (v: unknown) => {
  // A cell starting with = + - @ (or a tab/CR) is a formula to Excel; the text comes from LinkedIn
  // profiles, so it is defused with a leading apostrophe.
  const raw = v === null || v === undefined ? '' : `${v}`;
  const s = typeof v === 'string' && /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function qualityCsv(data: QualityExport): string {
  const musts = (data.brief?.mustHave || []).map((m) => m.label);
  const header = [
    'Page',
    'Position on page',
    'Name',
    'Recruiter profile',
    'Result',
    'Why not on the shortlist',
    'Reason',
    'List recommendation',
    ...musts.flatMap((m) => [`${m} — list verdict`, `${m} — list quote`, `${m} — full profile verdict`, `${m} — full profile quote`]),
    'Full profile read',
    'Sections opened',
    'Sections left closed',
    'Status now',
    'Recruiter decision',
    'Decision reason',
    'What the list card showed',
  ];
  const rows = data.rows.map((r) => [
    r.page,
    r.position,
    r.name,
    r.recruiterUrl,
    OUTCOME[r.outcome] || r.outcome,
    r.whyNot ?? '',
    r.reason,
    r.recommendation === 'talk_to' ? 'Talk to' : r.recommendation === 'needs_look' ? 'Needs your look' : r.recommendation === 'skip' ? 'Skip' : '',
    ...musts.flatMap((m) => {
      const list = r.listCriteria?.find((c) => c.label === m);
      const full = r.fullCriteria?.find((c) => c.label === m);
      return [list?.verdict ?? '', list?.quote ?? '', full?.verdict ?? '', full?.quote ?? ''];
    }),
    r.fullProfileRead ? 'yes' : 'no',
    r.sectionsOpened ?? '',
    r.sectionsLeftClosed ?? '',
    r.status,
    r.decision,
    r.decisionReason,
    r.cardText,
  ]);
  // A BOM so Excel opens the umlauts right.
  return `\uFEFF${[header, ...rows].map((row) => row.map(cell).join(',')).join('\r\n')}\r\n`;
}

export function downloadFile(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const qualityFileName = (data: QualityExport, ext: 'csv' | 'json') =>
  `sourcing-quality-${`${data.job.roleTitle || 'role'}`.replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 40)}-${`${data.job.createdAt}`.slice(0, 10)}.${ext}`;
